import React, { useState, useEffect, useRef, useCallback } from 'react';
import { VolumeX, Volume2, Pause, Play, MousePointer } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const ScreenReaderButton = () => {
  const [isReading, setIsReading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(true);
  const [voices, setVoices] = useState([]);
  const [isClickToReadMode, setIsClickToReadMode] = useState(false);
  const [highlightedElement, setHighlightedElement] = useState(null);
  const [showVoiceSelector, setShowVoiceSelector] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const { language } = useLanguage();
  const [settings] = useState({
    rate: 1.0,
    pitch: 1.0,
    volume: 1.0
  });

  const utteranceRef = useRef(null);
  const voiceSelectorRef = useRef(null);

  // Convert language context to speech language code
  const currentLanguage = language === 'ta' ? 'ta-IN' : 'en-IN';

  // Load voices
  useEffect(() => {
    if (!('speechSynthesis' in window)) {
      setSpeechSupported(false);
      return;
    }

const loadVoices = () => {
  const availableVoices = window.speechSynthesis.getVoices();
  if (availableVoices.length > 0) {
    // Filter out music/novelty voices and keep only professional voices
    const professionalVoices = availableVoices.filter(voice => {
      const name = voice.name.toLowerCase();
      const lang = voice.lang.toLowerCase();
      
      // Exclude novelty/music/low-quality voices
      const excludePatterns = [
        'bells', 'organ', 'whisper', 'bad', 'good', 'albert', 'bahh', 
        'bubbles', 'cellos', 'hysterical', 'junior', 'ralph', 'trinoids', 
        'zarvox', 'princess', 'superstar', 'rocko', 'shelley', 'deranged',
        'wobble', 'pipe', 'agnes', 'bruce', 'fred', 'kathy', 'vicki'
      ];
      const isNovelty = excludePatterns.some(pattern => name.includes(pattern));
      
      // Only include English and Tamil voices
      const isRelevantLanguage = lang.includes('en') || lang.includes('ta');
      
      // Prefer Google, Microsoft, Apple quality voices
      const isQualityVoice = name.includes('google') || 
                            name.includes('microsoft') || 
                            name.includes('enhanced') ||
                            name.includes('premium') ||
                            /samantha|alex|daniel|karen|moira|tessa|fiona|veena|rishi/i.test(name);
      
      return !isNovelty && isRelevantLanguage;
    });
    
    // Sort voices: quality voices first, then alphabetically
    const sortedVoices = professionalVoices.sort((a, b) => {
      const aQuality = a.name.toLowerCase().includes('google') || 
                      a.name.toLowerCase().includes('microsoft') ||
                      a.name.toLowerCase().includes('enhanced') ? 1 : 0;
      const bQuality = b.name.toLowerCase().includes('google') || 
                      b.name.toLowerCase().includes('microsoft') ||
                      b.name.toLowerCase().includes('enhanced') ? 1 : 0;
      
      if (aQuality !== bQuality) return bQuality - aQuality;
      return a.name.localeCompare(b.name);
    });
    
    setVoices(sortedVoices);
    
    // Set default voice if not already set - prefer enhanced/quality voices
    if (!selectedVoice && sortedVoices.length > 0) {
      const qualityVoice = sortedVoices.find(v => 
        v.name.toLowerCase().includes('enhanced') ||
        v.name.toLowerCase().includes('google') ||
        v.name.toLowerCase().includes('samantha') ||
        v.name.toLowerCase().includes('karen')
      );
      const defaultVoice = qualityVoice || sortedVoices.find(v => v.default) || sortedVoices[0];
      setSelectedVoice(defaultVoice);
    }
  }
};

    loadVoices();
    
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      window.speechSynthesis.cancel();
    };
  }, [selectedVoice]);

  // Close voice selector on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (voiceSelectorRef.current && !voiceSelectorRef.current.contains(event.target)) {
        setShowVoiceSelector(false);
      }
    };

    if (showVoiceSelector) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showVoiceSelector]);

  // Add click listener for click-to-read mode
  useEffect(() => {
    if (isClickToReadMode) {
      document.addEventListener('click', handleElementClick, true);
      document.body.style.cursor = 'pointer';
    } else {
      document.removeEventListener('click', handleElementClick, true);
      document.body.style.cursor = '';
      
      // Remove highlight when mode is disabled
      if (highlightedElement) {
        highlightedElement.style.outline = '';
        highlightedElement.style.backgroundColor = '';
        setHighlightedElement(null);
      }
    }

    return () => {
      document.removeEventListener('click', handleElementClick, true);
      document.body.style.cursor = '';
    };
  }, [isClickToReadMode, highlightedElement]);

  // Detect language of text content
  const detectLanguage = useCallback((text) => {
    // Check if text contains Tamil characters (Tamil Unicode range: 0B80-0BFF)
    const tamilPattern = /[\u0B80-\u0BFF]/;
    const hasTamil = tamilPattern.test(text);
    
    // Check if text contains English characters
    const englishPattern = /[a-zA-Z]/;
    const hasEnglish = englishPattern.test(text);
    
    // If has Tamil characters, use Tamil
    if (hasTamil) {
      return 'ta-IN';
    }
    
    // If only English, use English
    if (hasEnglish && !hasTamil) {
      return 'en-IN';
    }
    
    // Default to current language
    return currentLanguage;
  }, [currentLanguage]);

  // Get best voice for language
  const getBestVoice = useCallback((lang) => {
    if (voices.length === 0) return null;
    
    // If user has selected a voice, use that
    if (selectedVoice) {
      return selectedVoice;
    }
    
    if (lang === 'ta-IN') {
      // Find Tamil voice
      const tamilVoice = voices.find(v => 
        /ta-IN|tamil/i.test(v.lang) || /tamil/i.test(v.name)
      );
      return tamilVoice || null;
    } else {
      // Find English (India) voice
      const englishVoice = voices.find(v => 
        /en-IN|india/i.test(v.lang) || (/india/i.test(v.name) && /en/i.test(v.lang))
      );
      return englishVoice || null;
    }
  }, [voices, selectedVoice]);

  const getElementContent = useCallback((element) => {
    if (!element) return '';
    
    // Clone the element to avoid modifying the original
    const clone = element.cloneNode(true);
    
    // Remove non-content elements
    const elementsToRemove = clone.querySelectorAll(
      'script, style, .no-read, [aria-hidden="true"], button, input'
    );
    elementsToRemove.forEach(el => el.remove());
    
    // Get text content
    let text = clone.textContent || clone.innerText || '';
    
    // Clean up the text
    text = text
      .replace(/\s+/g, ' ')
      .replace(/\.\s*\./g, '.')
      .replace(/\.\s*,/g, ',')
      .replace(/,\s*\./g, '.')
      .trim();
    
    return text;
  }, []);

  const handleElementClick = useCallback((event) => {
    if (!isClickToReadMode) return;
    
    // Don't interfere with our own buttons
    if (event.target.closest('.screen-reader-controls')) {
      return;
    }
    
    event.preventDefault();
    event.stopPropagation();
    
    const clickedElement = event.target;
    
    // Remove previous highlight
    if (highlightedElement) {
      highlightedElement.style.outline = '';
      highlightedElement.style.backgroundColor = '';
    }
    
    // Highlight clicked element
    clickedElement.style.outline = '3px solid #1976D2';
    clickedElement.style.backgroundColor = 'rgba(25, 118, 210, 0.1)';
    setHighlightedElement(clickedElement);
    
    // Get and read the content
    const text = getElementContent(clickedElement);
    
    if (!text || text.length === 0) {
      console.warn('No readable content found in clicked element');
      return;
    }

    // Detect language from the text
    const detectedLang = detectLanguage(text);

    console.log('Reading clicked element:', text.substring(0, 200) + '...');
    console.log('Detected language:', detectedLang);

    // Stop any current speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utteranceRef.current = utterance;
    
    // Apply settings
    utterance.rate = settings.rate;
    utterance.pitch = settings.pitch;
    utterance.volume = settings.volume;
    utterance.lang = detectedLang;
    
    // Get best voice for detected language
    const bestVoice = getBestVoice(detectedLang);
    if (bestVoice) {
      utterance.voice = bestVoice;
    }

    // Event handlers
    utterance.onstart = () => {
      setIsReading(true);
      setIsPaused(false);
    };

    utterance.onend = () => {
      setIsReading(false);
      setIsPaused(false);
      utteranceRef.current = null;
    };

    utterance.onerror = (event) => {
      if (event.error !== 'interrupted') {
        console.error('Speech synthesis error:', event.error);
      }
      setIsReading(false);
      setIsPaused(false);
      utteranceRef.current = null;
    };

    utterance.onpause = () => {
      setIsPaused(true);
    };

    utterance.onresume = () => {
      setIsPaused(false);
    };

    window.speechSynthesis.speak(utterance);
  }, [isClickToReadMode, highlightedElement, getElementContent, detectLanguage, settings, getBestVoice]);

  const stopReading = useCallback(() => {
    window.speechSynthesis.cancel();
    setIsReading(false);
    setIsPaused(false);
    utteranceRef.current = null;
    
    // Remove highlight
    if (highlightedElement) {
      highlightedElement.style.outline = '';
      highlightedElement.style.backgroundColor = '';
      setHighlightedElement(null);
    }
  }, [highlightedElement]);

  const togglePause = useCallback(() => {
    if (isPaused) {
      window.speechSynthesis.resume();
    } else {
      window.speechSynthesis.pause();
    }
  }, [isPaused]);

  const toggleClickToReadMode = useCallback(() => {
    if (isClickToReadMode) {
      stopReading();
      setIsClickToReadMode(false);
    } else {
      setIsClickToReadMode(true);
    }
  }, [isClickToReadMode, stopReading]);

  if (!speechSupported) {
    return null;
  }

  return (
    <div className="relative flex items-center gap-2 screen-reader-controls">
      {/* Click-to-Read Mode Toggle */}
      <button
        onClick={toggleClickToReadMode}
        className={`h-10 w-10 flex items-center justify-center border-2 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#1976D2] focus:ring-offset-2 ${
          isClickToReadMode 
            ? 'border-[#1976D2] bg-[#1976D2] text-white hover:bg-[#1565C0]' 
            : 'border-[#1976D2] text-[#1976D2] bg-white hover:bg-[#E3F2FD]'
        }`}
        aria-label={isClickToReadMode ? "Disable click to read" : "Enable click to read"}
        title={isClickToReadMode ? "Click to Read: ON" : "Click to Read: OFF"}
      >
        <MousePointer size={18} />
      </button>

      {/* Voice Selector Button */}
      <div className="relative">
        <button
          onClick={() => setShowVoiceSelector(!showVoiceSelector)}
          className="h-10 px-3 flex items-center justify-center border-2 border-[#1976D2] text-[#1976D2] bg-white rounded-lg hover:bg-[#E3F2FD] transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#1976D2] focus:ring-offset-2 text-xs font-medium whitespace-nowrap"
          aria-label="Select voice"
          title="Change Voice"
        >
          <Volume2 size={16} className="mr-1" /> {language === 'ta' ? 'குரல்' : 'Voice'}
        </button>

        {/* Voice Selector Dropdown */}
        {showVoiceSelector && (
          <div 
            ref={voiceSelectorRef}
            className="absolute top-12 right-0 bg-white border-2 border-[#1976D2] rounded-lg shadow-xl w-72 max-h-96 overflow-y-auto z-50"
          >
            <div className="sticky top-0 bg-white border-b-2 border-[#1976D2] p-3">
              <h3 className="text-sm font-semibold text-gray-800">
                {language === 'ta' ? 'குரலைத் தேர்ந்தெடுக்கவும்' : 'Select Voice'}
              </h3>
            </div>
            
            <div className="p-2">
              {voices.length === 0 ? (
                <p className="text-xs text-gray-500 p-2">
                  {language === 'ta' ? 'குரல்கள் ஏதுமில்லை' : 'No voices available'}
                </p>
              ) : (
                voices.map((voice, index) => {
                  const isSelected = selectedVoice?.name === voice.name;
                  const isMale = voice.name.toLowerCase().includes('male') && !voice.name.toLowerCase().includes('female');
                  const isFemale = voice.name.toLowerCase().includes('female');
                  const genderIcon = isMale ? '👨' : isFemale ? '👩' : '🎙️';
                  
                  return (
                    <button
                      key={index}
                      onClick={() => {
                        setSelectedVoice(voice);
                        setShowVoiceSelector(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-md mb-1 transition-colors text-xs ${
                        isSelected 
                          ? 'bg-[#1976D2] text-white' 
                          : 'hover:bg-[#E3F2FD] text-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-base">{genderIcon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{voice.name}</div>
                          <div className={`text-xs ${isSelected ? 'text-blue-100' : 'text-gray-500'}`}>
                            {voice.lang}
                          </div>
                        </div>
                        {isSelected && <span className="text-white">✓</span>}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Stop Button (only show when reading) */}
      {isReading && (
        <button
          onClick={stopReading}
          className="h-10 w-10 flex items-center justify-center border-2 border-red-500 text-red-500 bg-white rounded-lg hover:bg-red-50 transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          aria-label="Stop reading"
          title="Stop"
        >
          <VolumeX size={18} />
        </button>
      )}

      {/* Pause/Resume Button */}
      {isReading && (
        <button
          onClick={togglePause}
          className="h-10 w-10 flex items-center justify-center border-2 border-[#1976D2] text-[#1976D2] bg-white rounded-lg hover:bg-[#E3F2FD] transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#1976D2] focus:ring-offset-2"
          aria-label={isPaused ? "Resume reading" : "Pause reading"}
          title={isPaused ? "Resume" : "Pause"}
        >
          {isPaused ? <Play size={18} /> : <Pause size={18} />}
        </button>
      )}

      {/* Click-to-Read Mode Indicator */}
      {isClickToReadMode && (
        <div className="absolute -bottom-8 right-0 bg-[#1976D2] text-white text-xs px-3 py-1 rounded-full shadow-lg whitespace-nowrap">
          {language === 'ta' ? 'படிக்க எங்கும் கிளிக் செய்யவும்' : 'Click anywhere to read'}
        </div>
      )}
    </div>
  );
};

export default ScreenReaderButton;